/**
 * /admin/ops/health — Platform Health (SCRUM-309): auto-verificación al
 * entrar, KPIs, agrupación por dominio real y drill-down por check.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { OpsSubnav } from "@/components/OpsSubnav";

type CheckStatus = "ok" | "warn" | "error" | "skip";
type HealthDomain = "dependencies" | "jobs" | "schema" | "configuration" | "platform" | "observability";

interface CheckResult {
  key: string;
  label: string;
  status: CheckStatus;
  detail: string;
  domain: HealthDomain;
  latencyMs?: number;
  meta?: Record<string, unknown>;
}

interface HealthReport {
  product: string;
  environment: string;
  timestamp: string;
  overall: CheckStatus;
  totalMs: number;
  checks: CheckResult[];
}

const STATUS_COLOR: Record<string, string> = {
  ok: "text-emerald-700 bg-emerald-50 border-emerald-200 dark:text-emerald-400 dark:bg-emerald-950/30 dark:border-emerald-900",
  warn: "text-amber-700 bg-amber-50 border-amber-200 dark:text-amber-400 dark:bg-amber-950/30 dark:border-amber-900",
  error: "text-red-700 bg-red-50 border-red-200 dark:text-red-400 dark:bg-red-950/30 dark:border-red-900",
  skip: "text-muted-foreground bg-muted border-border",
};

const STATUS_DOT: Record<string, string> = {
  ok: "bg-emerald-500",
  warn: "bg-amber-400",
  error: "bg-red-500",
  skip: "bg-muted-foreground/40",
};

const DOMAIN_LABELS: Record<HealthDomain, string> = {
  dependencies: "Dependencias",
  jobs: "Jobs",
  schema: "Schema",
  configuration: "Configuración",
  platform: "Plataforma",
  observability: "Observabilidad",
};

function KpiTile({ label, value, tone }: { label: string; value: number; tone?: "warn" | "error" }) {
  return (
    <div className="rounded-lg border border-border/60 p-3">
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p
        className={`text-xl font-semibold mt-0.5 ${
          tone === "error" && value > 0 ? "text-red-600" : tone === "warn" && value > 0 ? "text-amber-600" : ""
        }`}
      >
        {value}
      </p>
    </div>
  );
}

function CheckCard({ check }: { check: CheckResult }) {
  const [open, setOpen] = useState(false);
  const hasMeta = check.meta && Object.keys(check.meta).length > 0;
  return (
    <div className={`rounded-lg border p-3 ${STATUS_COLOR[check.status] ?? STATUS_COLOR.skip}`}>
      <button
        type="button"
        className="w-full text-left"
        onClick={() => hasMeta && setOpen((v) => !v)}
        disabled={!hasMeta}
      >
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full shrink-0 ${STATUS_DOT[check.status] ?? "bg-gray-300"}`} />
          <span className="font-medium text-sm">{check.label}</span>
          {check.latencyMs !== undefined && (
            <span className="ml-auto text-xs opacity-70 shrink-0">{check.latencyMs}ms</span>
          )}
          {hasMeta ? <span className="text-[10px] opacity-60 shrink-0">{open ? "▲" : "▼"}</span> : null}
        </div>
        <p className="text-xs opacity-80 mt-1">{check.detail}</p>
      </button>
      {open && hasMeta ? <CheckMeta check={check} /> : null}
    </div>
  );
}

function CheckMeta({ check }: { check: CheckResult }) {
  if (check.key === "cron_freshness") {
    const jobs = (check.meta?.jobs as Array<{ jobId: string; lastRun: string | null; status: string; ageMinutes: number | null }>) ?? [];
    if (jobs.length === 0) return <p className="text-xs opacity-70 mt-2">Sin jobs registrados.</p>;
    return (
      <ul className="mt-2 space-y-1 border-t border-current/20 pt-2">
        {jobs.map((j) => (
          <li key={j.jobId} className="text-[11px] font-mono flex justify-between gap-2">
            <span className="truncate">{j.jobId}</span>
            <span className="shrink-0 opacity-80">
              {j.status} · {j.ageMinutes != null ? `${j.ageMinutes}min` : "—"}
            </span>
          </li>
        ))}
      </ul>
    );
  }
  if (check.key === "env_sanity") {
    const checks = (check.meta?.checks as Array<{ key: string; present: boolean; required: boolean }>) ?? [];
    return (
      <ul className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 border-t border-current/20 pt-2">
        {checks.map((c) => (
          <li key={c.key} className="text-[11px] font-mono flex items-center gap-1.5">
            <span className={c.present ? "text-emerald-600" : c.required ? "text-red-600" : "opacity-50"}>
              {c.present ? "✓" : "✗"}
            </span>
            <span className="truncate">{c.key}</span>
          </li>
        ))}
      </ul>
    );
  }
  return (
    <pre className="mt-2 text-[10px] font-mono overflow-x-auto border-t border-current/20 pt-2">
      {JSON.stringify(check.meta, null, 1)}
    </pre>
  );
}

export const Route = createFileRoute("/admin/ops/health")({
  component: PlatformHealthPage,
});

function PlatformHealthPage() {
  const [report, setReport] = useState<HealthReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ranOnce = useRef(false);

  async function runCheck() {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/ops/health/full", { headers: { accept: "application/json" } });
      const data = await res.json();
      setReport(data);
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (ranOnce.current) return;
    ranOnce.current = true;
    void runCheck();
  }, []);

  const kpis = useMemo(() => {
    const checks = report?.checks ?? [];
    return {
      total: checks.length,
      ok: checks.filter((c) => c.status === "ok").length,
      warn: checks.filter((c) => c.status === "warn").length,
      error: checks.filter((c) => c.status === "error").length,
    };
  }, [report]);

  const grouped = useMemo(() => {
    const checks = report?.checks ?? [];
    const byDomain = new Map<HealthDomain, CheckResult[]>();
    for (const c of checks) {
      const arr = byDomain.get(c.domain) ?? [];
      arr.push(c);
      byDomain.set(c.domain, arr);
    }
    return Array.from(byDomain.entries());
  }, [report]);

  return (
    <div className="p-4 sm:p-6 space-y-4 max-w-3xl mx-auto">
      <OpsSubnav hint="Diagnóstico operativo de la plataforma — se ejecuta automáticamente al entrar." />

      <div className="flex items-center justify-between gap-2">
        <div>
          <h1 className="text-xl font-semibold">Platform Health</h1>
          <p className="text-sm text-muted-foreground">Estado de infraestructura en tiempo real</p>
        </div>
        <button
          onClick={() => void runCheck()}
          disabled={loading}
          className="px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 shrink-0"
        >
          {loading ? "Verificando…" : "Verificar ahora"}
        </button>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>
      )}

      {loading && !report ? (
        <div className="text-center py-12 text-muted-foreground text-sm">Ejecutando checks…</div>
      ) : null}

      {report && (
        <div className="space-y-4">
          <div className={`rounded-lg border p-4 ${STATUS_COLOR[report.overall]}`}>
            <div className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded-full ${STATUS_DOT[report.overall]}`} />
              <span className="font-semibold">Estado general: {report.overall.toUpperCase()}</span>
              <span className="ml-auto text-xs opacity-70">{report.totalMs}ms total</span>
            </div>
            <p className="text-xs mt-1 opacity-70">
              {report.product} · {report.environment} · {new Date(report.timestamp).toLocaleString("es-AR")}
            </p>
          </div>

          <div className="grid grid-cols-4 gap-2">
            <KpiTile label="Checks" value={kpis.total} />
            <KpiTile label="OK" value={kpis.ok} />
            <KpiTile label="Warn" value={kpis.warn} tone="warn" />
            <KpiTile label="Error" value={kpis.error} tone="error" />
          </div>

          {grouped.map(([domain, domainChecks]) => (
            <div key={domain} className="space-y-2">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {DOMAIN_LABELS[domain]}
              </h3>
              <div className="grid gap-2 sm:grid-cols-2">
                {domainChecks.map((c) => (
                  <CheckCard key={c.key} check={c} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
